/**
 * Klaviyo relay, shared by the footer newsletter band and the email popup.
 *
 * The theme's newsletter forms are plain, theme-styled forms, but Klaviyo has
 * to own the subscription so the store keeps its form analytics, list
 * attribution and welcome flow. Each theme form therefore sits next to a
 * visually hidden Klaviyo embed. This relay types the address into that embed,
 * presses its button and reports what Klaviyo did with it:
 *
 *   success      Klaviyo dispatched its `klaviyoForms` window event with type
 *                stepSubmit/submit for this form. Klaviyo only does that after
 *                its subscriptions API accepted the signup, and before it
 *                re-renders the embed.
 *   invalid      Klaviyo's own validation flagged the field (aria-invalid), so
 *                nothing was sent. Soft: the form stays open for another try.
 *   timeout      Nothing happened within TIMEOUT_MS. Soft: a signup that lands
 *                later still reports success.
 *   rejected     Klaviyo swapped its form for an error view without a success
 *                event, so the request failed or was blocked. Terminal: the
 *                embed cannot be resubmitted without a reload.
 *   unavailable  Klaviyo never mounted into the host. Its embeds appear a few
 *                seconds after the page loads, so a submit that arrives first
 *                waits up to MOUNT_WAIT_MS for them (longer while the page is
 *                still loading); only a script that is blocked (ad blockers,
 *                privacy browsers) or down ends here.
 *   loading      Klaviyo never mounted either, but it is still on its way: the
 *                page has not finished loading, or Klaviyo's script did run.
 *                Slow connections end here rather than being told to turn off
 *                an ad blocker they don't have.
 *
 * The hidden embeds are also kept out of the way: their "Sign up for our
 * newsletter" heading is turned into a paragraph, so it doesn't count as an
 * extra main heading on every page, and their field and button leave the Tab
 * order. Klaviyo sets that heading as one piece of rich-text HTML, so swapping
 * it does not disturb Klaviyo's own rendering.
 *
 * Usage:
 *   window.csKlaviyoRelay.submit({ host, email, onSuccess, onError })
 * where `host` wraps the `.klaviyo-form-XXXXXX` mount and carries
 * data-klaviyo-form-id="XXXXXX". `onError(message, kind)` may be followed by
 * `onSuccess()` for the soft kinds above.
 */
(function () {
  'use strict';

  var TIMEOUT_MS = 20000;
  var MOUNT_WAIT_MS = 10000;
  var SLOW_PAGE_MOUNT_WAIT_MS = 30000;
  /* Klaviyo fires its success event before it re-renders, so a re-render with
     no event inside this window is an error view. */
  var ERROR_GRACE_MS = 750;
  /* Lets Klaviyo's React state absorb the typed value before the click, and
     the click's validation render before it is inspected. */
  var SETTLE_MS = 60;
  var SUCCESS_TYPES = { stepSubmit: true, submit: true };
  var TERMINAL = { success: true, rejected: true };

  var MESSAGES = {
    unavailable: "Sign-up couldn't load. If you use an ad blocker or a privacy browser, please allow this site and try again.",
    loading: 'Sign-up is still loading. Please try again in a moment.',
    invalid: 'Please check the email address and try again.',
    rejected: "Sorry, that didn't go through. Please refresh the page and try again.",
    timeout: 'This is taking longer than expected. Please try again in a moment.'
  };

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  }

  /* Klaviyo's script sets its __kla_id cookie when it runs, and a script that loaded shows
     up in the page's resource timings. Blocked scripts leave neither. */
  function klaviyoRan() {
    if (/(?:^|;\s*)__kla_id=/.test(document.cookie)) return true;
    try {
      return performance.getEntriesByType('resource').some(function (r) {
        return r.name.indexOf('klaviyo.com/onsite/js/') !== -1;
      });
    } catch (e) {
      return false;
    }
  }

  function mountFailureKind() {
    return document.readyState !== 'complete' || klaviyoRan() ? 'loading' : 'unavailable';
  }

  function quietHost(host) {
    host.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(function (heading) {
      var p = document.createElement('p');
      if (heading.hasAttribute('style')) p.setAttribute('style', heading.getAttribute('style'));
      while (heading.firstChild) p.appendChild(heading.firstChild);
      heading.replaceWith(p);
    });
    host.querySelectorAll('input, button, select, textarea, a[href]').forEach(function (el) {
      if (el.getAttribute('tabindex') !== '-1') el.setAttribute('tabindex', '-1');
    });
  }

  function quietHosts() {
    document.querySelectorAll('[data-klaviyo-form-id]').forEach(function (host) {
      if (host.csQuietObserver) return;
      quietHost(host);
      host.csQuietObserver = new MutationObserver(function () { quietHost(host); });
      host.csQuietObserver.observe(host, { childList: true, subtree: true });
    });
  }

  function findControls(host) {
    var input = host.querySelector('input[type="email"], input[type="text"]');
    var button = host.querySelector('button:not([aria-label="Close"])');
    return input && button ? { input: input, button: button } : null;
  }

  /* Klaviyo renders its form with React, which ignores a plain `.value = x`.
     Go through the prototype setter and let the input event carry the value. */
  function setInputValue(input, value) {
    var setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    setter.call(input, value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function submit(opts) {
    var host = opts.host;
    var formId = host ? host.getAttribute('data-klaviyo-form-id') : null;

    /* One relay per host: a resubmit replaces the previous attempt's listeners,
       and any late success is picked up by the new attempt instead. */
    if (host && host.csRelayCancel) host.csRelayCancel();

    if (!formId) {
      opts.onError(MESSAGES.unavailable, 'unavailable');
      return;
    }

    var controls = findControls(host);
    if (controls) {
      relay(host, formId, controls, opts);
      return;
    }

    /* Not mounted yet: watch the host until Klaviyo fills it, or give up. */
    var mountObserver = new MutationObserver(function () {
      var mounted = findControls(host);
      if (!mounted) return;
      stopWaiting();
      relay(host, formId, mounted, opts);
    });
    var waitStart = Date.now();
    var mountTimer = null;
    function waitForMount(ms) {
      mountTimer = setTimeout(function () {
        /* Klaviyo's script arrives late on a slow connection, so a page that is still
           loading gets longer before the submit is given up on. */
        if (document.readyState !== 'complete' && Date.now() - waitStart < SLOW_PAGE_MOUNT_WAIT_MS) {
          waitForMount(1000);
          return;
        }
        stopWaiting();
        var kind = mountFailureKind();
        opts.onError(MESSAGES[kind], kind);
      }, ms);
    }
    waitForMount(MOUNT_WAIT_MS);
    function stopWaiting() {
      mountObserver.disconnect();
      clearTimeout(mountTimer);
      if (host.csRelayCancel === stopWaiting) host.csRelayCancel = null;
    }
    host.csRelayCancel = stopWaiting;
    mountObserver.observe(host, { childList: true, subtree: true });
  }

  function relay(host, formId, controls, opts) {
    var done = false;
    var clicked = false;
    var lastReported = null;
    var graceTimer = null;
    var timeoutTimer = null;
    var observer = null;

    function cleanup() {
      window.removeEventListener('klaviyoForms', onKlaviyoEvent);
      if (observer) observer.disconnect();
      clearTimeout(graceTimer);
      clearTimeout(timeoutTimer);
      if (host.csRelayCancel === cleanup) host.csRelayCancel = null;
    }

    function report(kind) {
      /* A soft outcome is reported once per attempt: the same invalid field is
         seen by both the observer and the post-click inspection. */
      if (done || kind === lastReported) return;
      lastReported = kind;
      if (TERMINAL[kind]) {
        done = true;
        cleanup();
      }
      if (kind === 'success') opts.onSuccess();
      else opts.onError(MESSAGES[kind], kind);
    }

    function onKlaviyoEvent(e) {
      var detail = e.detail || {};
      if (detail.formId === formId && SUCCESS_TYPES[detail.type]) report('success');
    }

    function inspectHost() {
      if (!clicked || done) return;
      if (host.querySelector('[aria-invalid="true"]')) {
        report('invalid');
        return;
      }
      if (!host.contains(controls.input) && graceTimer === null) {
        graceTimer = setTimeout(function () { report('rejected'); }, ERROR_GRACE_MS);
      }
    }

    host.csRelayCancel = cleanup;
    window.addEventListener('klaviyoForms', onKlaviyoEvent);
    observer = new MutationObserver(inspectHost);
    observer.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-invalid']
    });
    timeoutTimer = setTimeout(function () { report('timeout'); }, TIMEOUT_MS);

    setInputValue(controls.input, opts.email);
    setTimeout(function () {
      clicked = true;
      try {
        controls.button.click();
      } catch (e) {
        /* Klaviyo's handler threw; the timeout reports it. */
      }
      setTimeout(inspectHost, SETTLE_MS);
    }, SETTLE_MS);
  }

  window.csKlaviyoRelay = { submit: submit, isValidEmail: isValidEmail };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', quietHosts);
  else quietHosts();
})();
